import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { tcPost } from './TimeClockDialogs';
import {
    dayLabel, dayStatusLabel, dayStatusTone, errorText, firstOfMonth, hm, hmSigned, isoDate, monthLabel, time, tone,
} from './tcFormat';

/**
 * Hours for a period: every day, or totals per ISO week or per month.
 * A day row opens its stamps.
 */
const TimeClockHoursTab = ({ t, lang, theme, api, storeId, storeName, reloadKey, onLoaded, onDay }) => {
    const today = isoDate(new Date());
    const [group, setGroup] = useState(() => {
        try { return window.sessionStorage.getItem('tc_hours_group') || 'day'; } catch (e) { return 'day'; }
    });
    const [from, setFrom] = useState(firstOfMonth(today));
    const [to, setTo] = useState(today);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        try { window.sessionStorage.setItem('tc_hours_group', group); } catch (e) { /* private mode */ }
    }, [group]);

    useEffect(() => {
        if (!from || !to || from > to) return;
        setLoading(true);
        setError('');
        tcPost('hours', api, { from_date: from, to_date: to, group, store_id: storeId || '' }).then((response) => {
            setLoading(false);
            if (response?.success) {
                setRows((response.data?.rows || []).map((row, i) => ({ ...row, _id: (row.work_date || row.period) + '|' + row.user_id + '|' + i })));
                if (onLoaded) onLoaded();
            } else {
                setRows([]);
                setError(errorText(t, response));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, from, to, group, storeId, reloadKey]);

    const num = { type: 'number', align: 'right', headerAlign: 'right' };

    const dayColumns = [
        { field: 'work_date', headerName: t('tc_date', 'Date'), width: 110, valueFormatter: (value) => dayLabel(value, lang) },
        { field: 'user_name', headerName: t('tc_employee', 'Employee'), flex: 1, minWidth: 160 },
        { field: 'store_id', headerName: t('tc_store', 'Store'), width: 140, valueFormatter: (value) => storeName(value) },
        { field: 'first_in', headerName: t('tc_first_in', 'First in'), width: 90, valueFormatter: (value) => time(value) },
        { field: 'last_out', headerName: t('tc_last_out', 'Last out'), width: 90, valueFormatter: (value) => time(value) },
        { field: 'break_minutes', headerName: t('tc_break', 'Break'), width: 80, ...num, valueFormatter: (value) => hm(value, lang) },
        { field: 'worked_minutes', headerName: t('tc_worked', 'Worked'), width: 90, ...num, valueFormatter: (value) => hm(value, lang) },
        {
            field: 'status', headerName: t('tc_status', 'Status'), width: 120, display: 'flex',
            renderCell: (params) => (
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: dayStatusTone(params.value) }}>{dayStatusLabel(t, params.value)}</Typography>
            ),
        },
        {
            field: 'anomaly_flags', headerName: t('tc_notes', 'Notes'), flex: 1, minWidth: 160, sortable: false, display: 'flex',
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {(params.value || []).filter((f) => f !== 'IMPORTED').map((flag) => (
                        <Chip key={flag} size="small" label={flagLabel(t, flag)} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    ))}
                    {params.row.correction_count > 0 && <Chip size="small" label={'✎ ' + params.row.correction_count} sx={{ height: 20, fontSize: 11 }} />}
                </Box>
            ),
        },
    ];

    const periodColumns = [
        {
            field: 'period', headerName: group === 'week' ? t('tc_week_cap', 'Week') : t('tc_month', 'Month'), width: 150,
            valueFormatter: (value, row) => (group === 'month' ? monthLabel(row.from_date, lang) : value.replace('-W', ' · W')),
        },
        { field: 'user_name', headerName: t('tc_employee', 'Employee'), flex: 1, minWidth: 160 },
        { field: 'days_worked', headerName: t('tc_days', 'Days'), width: 70, ...num },
        { field: 'break_minutes', headerName: t('tc_break', 'Break'), width: 90, ...num, valueFormatter: (value) => hm(value, lang) },
        { field: 'worked_minutes', headerName: t('tc_worked', 'Worked'), width: 100, ...num, valueFormatter: (value) => hm(value, lang) },
        { field: 'contract_minutes', headerName: t('tc_contract', 'Contract'), width: 100, ...num, valueFormatter: (value) => (value === null ? '' : hm(value, lang)) },
        {
            field: 'difference_minutes', headerName: t('tc_difference', 'Difference'), width: 110, ...num, display: 'flex',
            renderCell: (params) => (params.value === null ? '' : (
                <Typography sx={{ fontSize: 13.5, color: params.value < 0 ? tone.bad : params.value > 0 ? tone.ok : 'inherit' }}>{hmSigned(params.value, lang)}</Typography>
            )),
        },
        { field: 'incomplete_days', headerName: t('tc_status_incomplete', 'Incomplete'), width: 110, ...num },
        { field: 'corrected_days', headerName: t('tc_status_corrected', 'Corrected'), width: 110, ...num },
    ];

    const totalWorked = rows.reduce((sum, row) => sum + (row.worked_minutes || 0), 0);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup exclusive size="small" value={group} onChange={(e, v) => v && setGroup(v)}>
                    <ToggleButton value="day" sx={{ textTransform: 'none', px: 1.75, height: 40, fontWeight: 600 }}>{t('tc_per_day', 'Per day')}</ToggleButton>
                    <ToggleButton value="week" sx={{ textTransform: 'none', px: 1.75, height: 40, fontWeight: 600 }}>{t('tc_per_week', 'Per week')}</ToggleButton>
                    <ToggleButton value="month" sx={{ textTransform: 'none', px: 1.75, height: 40, fontWeight: 600 }}>{t('tc_per_month', 'Per month')}</ToggleButton>
                </ToggleButtonGroup>
                <TextField size="small" type="date" label={t('tc_from', 'From')} value={from} onChange={(e) => setFrom(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to } }} sx={{ width: 160 }} />
                <TextField size="small" type="date" label={t('tc_to', 'To')} value={to} onChange={(e) => setTo(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from } }} sx={{ width: 160 }} />
                <Box sx={{ flex: 1 }} />
                <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{t('tc_total', 'Total')}</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{hm(totalWorked, lang)}</Typography>
                </Box>
            </Box>
            {error && <Typography sx={{ color: tone.bad, fontSize: 14, mb: 1 }}>{error}</Typography>}
            <Box sx={{ height: 'calc(100vh - 340px)', minHeight: 420 }}>
                <DataGridPro
                    className={theme}
                    rows={rows}
                    getRowId={(row) => row._id}
                    columns={group === 'day' ? dayColumns : periodColumns}
                    loading={loading}
                    density="compact"
                    disableRowSelectionOnClick
                    onRowClick={group === 'day' ? (params) => onDay(params.row) : undefined}
                    sx={{ fontSize: 13.5, borderRadius: '10px', '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 }, '& .MuiDataGrid-row': { cursor: group === 'day' ? 'pointer' : 'default' } }}
                    localeText={{ noRowsLabel: t('tc_no_days', 'No stamps in this period.') }}
                />
            </Box>
        </Box>
    );
};

export const flagLabel = (t, flag) => ({
    MISSING_IN: t('tc_flag_missing_in', 'IN missing'),
    MISSING_OUT: t('tc_flag_missing_out', 'OUT missing'),
    LONG_SHIFT: t('tc_tag_long', 'Long shift'),
    BREAK_DEDUCTED: t('tc_flag_break_deducted', 'Break deducted'),
    PENDING_CORRECTION: t('tc_request_waiting', 'request waiting'),
    LATE_SYNC: t('tc_flag_late_sync', 'Sent late'),
    CLOCK_SKEW: t('tc_flag_clock_skew', 'Device clock off'),
    AUTO_CLOSED: t('tc_flag_auto_closed', 'Closed automatically'),
    NO_ACCEPTED_STAMP: t('tc_flag_no_stamp', 'No valid stamp'),
    IDENTITY_UNCERTAIN: t('tc_flag_identity', 'Person uncertain'),
    LOCATION_DOUBT: t('tc_flag_location_doubt', 'Location uncertain'),
    LOCATION_UNCHECKED: t('tc_flag_location_unchecked', 'Location not checked'),
}[flag] || flag);

export default TimeClockHoursTab;
